import { createTheme } from '@mui/material/styles';

// Create custom MUI theme to match the existing Ant Design styling
const muiTheme = createTheme({
  direction: 'rtl', // Right-to-left support for Arabic
  
  palette: {
    primary: {
      main: '#1677ff',
      light: '#4096ff',
      dark: '#0958d9',
      contrastText: '#fff',
    },
    secondary: {
      main: '#52c41a',
      light: '#73d13d',
      dark: '#389e0d',
      contrastText: '#fff',
    },
    error: {
      main: '#ff4d4f',
      light: '#ff7875',
      dark: '#cf1322',
    },
    warning: {
      main: '#faad14',
      light: '#ffc53d',
      dark: '#d48806',
    },
    success: {
      main: '#52c41a',
      light: '#73d13d',
      dark: '#389e0d',
    },
    background: {
      default: '#ffffff',
      paper: '#fafafa',
    },
    text: {
      primary: 'rgba(0, 0, 0, 0.88)',
      secondary: 'rgba(0, 0, 0, 0.65)',
      disabled: 'rgba(0, 0, 0, 0.25)',
    },
    divider: 'rgba(5, 5, 5, 0.06)',
  },
  
  typography: {
    fontFamily: [
      'Inter',
      'Poppins',
      '-apple-system',
      'BlinkMacSystemFont',
      'Segoe UI',
      'Roboto',
      'Helvetica Neue',
      'Arial',
      'Noto Sans',
      'sans-serif',
      'Apple Color Emoji',
      'Segoe UI Emoji',
      'Segoe UI Symbol',
      'Noto Color Emoji',
    ].join(','),
    fontSize: 14,
    h1: {
      fontSize: '3rem',
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
      fontFamily: '"Inter", "Poppins", sans-serif',
    },
    h2: {
      fontSize: '2.25rem',
      fontWeight: 700,
      lineHeight: 1.25,
      letterSpacing: '-0.01em',
      fontFamily: '"Inter", "Poppins", sans-serif',
    },
    h3: {
      fontSize: '1.875rem',
      fontWeight: 600,
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
      fontFamily: '"Inter", "Poppins", sans-serif',
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.35,
      letterSpacing: '0em',
      fontFamily: '"Inter", "Poppins", sans-serif',
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: 1.4,
      letterSpacing: '0em',
      fontFamily: '"Inter", "Poppins", sans-serif',
    },
    h6: {
      fontSize: '1.125rem',
      fontWeight: 600,
      lineHeight: 1.45,
      letterSpacing: '0em',
      fontFamily: '"Inter", "Poppins", sans-serif',
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
      fontWeight: 400,
      letterSpacing: '0.01em',
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.65,
      fontWeight: 400,
      letterSpacing: '0.01em',
    },
    button: {
      fontSize: '0.875rem',
      fontWeight: 500,
      lineHeight: 1.5,
      textTransform: 'none',
      letterSpacing: '0.02em',
    },
    subtitle1: {
      fontSize: '1.125rem',
      fontWeight: 500,
      lineHeight: 1.5,
      letterSpacing: '0.01em',
      fontFamily: '"Inter", "Poppins", sans-serif',
    },
    subtitle2: {
      fontSize: '1rem',
      fontWeight: 500,
      lineHeight: 1.55,
      letterSpacing: '0.01em',
      fontFamily: '"Inter", "Poppins", sans-serif',
    },
  },
  
  shape: {
    borderRadius: 6,
  },
  
  spacing: 8,
  
  components: {
    // Override MUI components to match Ant Design styling
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 400,
          textTransform: 'none',
          boxShadow: 'none',
          backgroundColor: '#3b5dac',
          color: '#fff',
          '&:hover': {
            backgroundColor: '#2a406e',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
          },
        },
        contained: {
          backgroundColor: '#3b5dac',
          color: '#fff',
          '&:hover': {
            backgroundColor: '#2a406e',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.12)',
          },
        },
        outlined: {
          borderColor: '#3b5dac',
          color: '#fff',
          backgroundColor: '#3b5dac',
          '&:hover': {
            backgroundColor: '#2a406e',
            borderColor: '#2a406e',
            color: '#fff',
          },
        },
        text: {
          color: '#fff',
          backgroundColor: '#3b5dac',
          '&:hover': {
            backgroundColor: '#2a406e',
            color: '#fff',
          },
        },
      },
    },
    
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          border: '1px solid rgba(5, 5, 5, 0.06)',
          boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.03), 0 1px 6px -1px rgba(0, 0, 0, 0.02), 0 2px 4px 0 rgba(0, 0, 0, 0.02)',
          '&:hover': {
            boxShadow: '0 4px 12px 0 rgba(0, 0, 0, 0.05), 0 2px 8px -2px rgba(0, 0, 0, 0.05), 0 6px 16px 0 rgba(0, 0, 0, 0.08)',
          },
        },
      },
    },
    
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 6,
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#4096ff',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#1677ff',
              borderWidth: 1,
            },
          },
        },
      },
    },
    
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRadius: '8px 0 0 8px',
        },
      },
    },
    
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#1e3a8a',
          color: 'rgba(0, 0, 0, 0.88)',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          borderBottom: '1px solid #f0f0f0',
        },
      },
    },
    
    MuiPaper: {
      styleOverrides: {
        elevation1: {
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        },
        elevation4: {
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        },
      },
    },
    
    MuiBadge: {
      styleOverrides: {
        badge: {
          fontSize: '10px',
          fontWeight: 600,
          minWidth: '16px',
          height: '16px',
        },
      },
    },
  },
  
  // Custom breakpoints to match existing responsive design
  breakpoints: {
    values: {
      xs: 0,
      sm: 576,
      md: 768,
      lg: 992,
      xl: 1200,
    },
  },
});

export { muiTheme as theme };
export default muiTheme;
